/**
 * Streaming client for the roundtable endpoints (§7h).
 *
 * Two backend routes on the same chat Function URL, differentiated by
 * `isFollowUp`:
 *   - `POST /roundtable`                       — create a new roundtable turn
 *   - `POST /roundtable/{id}/message`          — follow-up on an existing one
 *
 * The backend's SSE event schema (shipped in 7d) is richer than the
 * single-chat stream — handled via per-event callbacks:
 *
 *   roundtable_started → onStarted({ conversationId, personaIds, followUp })
 *   roundtable_speaker → onSpeaker({ personaId, personaName, index })
 *   token with personaId → onToken(personaId, token)
 *   error with recoverable=true → onPanelError({ personaId, error })
 *   error with recoverable=false AND dailyTokenBudget → onBudgetExhausted(err)
 *   error (any other) → onError(err)
 *   roundtable_done → onDone({ conversationId, title?, respondedPersonaIds, budgetExhausted })
 */

import { streamSSE, taggedBudgetError } from "./sse.js";

const CHAT_ENDPOINT = import.meta.env.VITE_CHAT_ENDPOINT.replace(/\/$/, "");

export async function streamRoundtable({
  personaIds,
  projectId,
  conversationId,
  message,
  targetPersonaId,
  isFollowUp,
  signal,
  onStarted,
  onSpeaker,
  onToken,
  onPanelError,
  onBudgetExhausted,
  onDone,
  onError,
}) {
  const url = isFollowUp
    ? `${CHAT_ENDPOINT}/roundtable/${encodeURIComponent(conversationId)}/message`
    : `${CHAT_ENDPOINT}/roundtable`;

  const body = isFollowUp
    ? { message, ...(targetPersonaId ? { targetPersonaId } : {}) }
    : {
        personaIds,
        message,
        ...(conversationId ? { conversationId } : {}),
        ...(projectId ? { projectId } : {}),
      };

  await streamSSE({
    url,
    body,
    signal,
    onError,
    onEvent(data) {
      if (data.roundtable_started) {
        onStarted?.({
          conversationId: data.conversationId,
          personaIds: data.personaIds,
          followUp: !!data.followUp,
        });
        return;
      }

      if (data.roundtable_speaker) {
        onSpeaker?.({
          personaId: data.personaId,
          personaName: data.personaName,
          index: data.index,
        });
        return;
      }

      if (data.roundtable_done) {
        onDone?.({
          conversationId: data.conversationId,
          title: data.title,
          respondedPersonaIds: data.respondedPersonaIds,
          budgetExhausted: !!data.budgetExhausted,
        });
        return { bail: true };
      }

      if (data.error) {
        if (data.recoverable === true && data.personaId) {
          onPanelError?.({
            personaId: data.personaId,
            error: data.error,
          });
          return;
        }
        const budgetErr = taggedBudgetError(data);
        if (budgetErr) {
          onBudgetExhausted?.(budgetErr);
          return;
        }
        return { bail: true, error: new Error(data.error) };
      }

      if (data.token && data.personaId) {
        onToken?.(data.personaId, data.token);
      }
    },
  });
}
